import fontkit from '@pdf-lib/fontkit';
import { PDFDocument, PDFFont, StandardFonts } from 'pdf-lib';

/** Noto Sans LGC TTF (hinted) — Latin, Greek, Cyrillic + extensive Unicode. */
const NOTO_REGULAR =
  'https://cdn.jsdelivr.net/gh/notofonts/latin-greek-cyrillic@main/fonts/NotoSans/hinted/ttf/NotoSans-Regular.ttf';
const NOTO_BOLD =
  'https://cdn.jsdelivr.net/gh/notofonts/latin-greek-cyrillic@main/fonts/NotoSans/hinted/ttf/NotoSans-Bold.ttf';
const NOTO_ITALIC =
  'https://cdn.jsdelivr.net/gh/notofonts/latin-greek-cyrillic@main/fonts/NotoSans/hinted/ttf/NotoSans-Italic.ttf';
const NOTO_BOLDITALIC =
  'https://cdn.jsdelivr.net/gh/notofonts/latin-greek-cyrillic@main/fonts/NotoSans/hinted/ttf/NotoSans-BoldItalic.ttf';

export interface EmbeddedBodyFonts {
  regular: PDFFont;
  bold: PDFFont;
  italic: PDFFont;
  boldItalic: PDFFont;
  mono: PDFFont;
}

interface NotoBytes {
  regular: ArrayBuffer;
  bold: ArrayBuffer;
  italic: ArrayBuffer;
  boldItalic: ArrayBuffer;
}

let notoBytesPromise: Promise<NotoBytes> | null = null;

async function fetchFont(url: string): Promise<ArrayBuffer> {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`Font fetch failed: ${url}`);
  return res.arrayBuffer();
}

async function loadNotoBytes(): Promise<NotoBytes> {
  if (!notoBytesPromise) {
    notoBytesPromise = (async () => {
      const [regular, bold, italic, boldItalic] = await Promise.all([
        fetchFont(NOTO_REGULAR),
        fetchFont(NOTO_BOLD),
        fetchFont(NOTO_ITALIC),
        fetchFont(NOTO_BOLDITALIC),
      ]);
      return { regular, bold, italic, boldItalic };
    })();
  }
  try {
    return await notoBytesPromise;
  } catch (e) {
    notoBytesPromise = null;
    throw e;
  }
}

/**
 * Embeds Noto Sans + Courier (monospace) for the given PDF document.
 * Font bytes are fetched once per session; embedding is per document.
 */
export async function embedBodyFonts(doc: PDFDocument): Promise<EmbeddedBodyFonts> {
  doc.registerFontkit(fontkit);
  const bytes = await loadNotoBytes();
  const [regular, bold, italic, boldItalic] = await Promise.all([
    doc.embedFont(bytes.regular, { subset: true }),
    doc.embedFont(bytes.bold, { subset: true }),
    doc.embedFont(bytes.italic, { subset: true }),
    doc.embedFont(bytes.boldItalic, { subset: true }),
  ]);
  const mono = await doc.embedFont(StandardFonts.Courier);
  return { regular, bold, italic, boldItalic, mono };
}

export async function embedStandardFallbackFonts(doc: PDFDocument): Promise<EmbeddedBodyFonts> {
  const regular = await doc.embedFont(StandardFonts.Helvetica);
  const bold = await doc.embedFont(StandardFonts.HelveticaBold);
  const italic = await doc.embedFont(StandardFonts.HelveticaOblique);
  const boldItalic = await doc.embedFont(StandardFonts.HelveticaBoldOblique);
  const mono = await doc.embedFont(StandardFonts.Courier);
  return { regular, bold, italic, boldItalic, mono };
}
